import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { ExternalLink } from 'react-feather';
import { useForm } from 'react-form-state-manager';
import { useMemo } from 'react';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { AddParticipantFieldImportMutation as AddParticipantFieldImport } from '__generated__/graphql';
import { getServerErrors } from '../../../common/helpers';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import AddParticipantFieldImportMutation from './AddParticipantFieldImportMutation';
import EventPicker from '../../Events/EventPicker';
import GetParticipantFieldImportsQuery from './GetParticipantFieldImportsQuery';
import GetParticipantFieldsForImportQuery from './GetParticipantFieldsForImportQuery';
import ParticipantFieldImportExample from './ParticipantFieldImportExample';
import UI from '../../../common/UI';
import useMutation from '../../../api/useMutation';
import useProject from '../../useProject';
import useQuery from '../../../api/useQuery';
import useValidation from '../../../common/useValidation';

type ParticipantFieldImport = AddParticipantFieldImport['addParticipantFieldImport'];

interface ImportData {
  type: 'participants' | 'participant_field_entries' | null;
  event: { id: string; } | null;
  participant_field: { id: string; } | null;
  data: any;
}

export interface AddParticipantFieldImportFormProps {
  eventId?: string;
  onSuccess?: (participantFieldImport: ParticipantFieldImport) => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

const AddParticipantFieldImportForm = ({
  eventId, onSuccess, onCancel, refetchQueries = [GetParticipantFieldImportsQuery],
}: AddParticipantFieldImportFormProps) => {
  const { t } = useTranslation();
  const notifier = useNotifier();
  const project = useProject();

  const form = useForm<ImportData>({
    values: {
      type: null,
      event: {
        id: eventId,
      },
      participant_field: null,
      data: null,
    },
  });

  const importParticipants = form.values.type === 'participants';
  const importParticipantFieldEntries = form.values.type === 'participant_field_entries';

  const [
    addImport, { loading, error },
  ] = useMutation(
    AddParticipantFieldImportMutation,
    {
      variables: {
        input: {
          event: { id: form.values.event.id },
          participant_field: importParticipantFieldEntries ? { id: form.values.participant_field?.id } : null,
          data: form.values.data,
        },
      },
      onCompleted: ({ addParticipantFieldImport }) => {
        notifier.success(t('notifications.import_added'));
        onSuccess?.(addParticipantFieldImport);
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  const { data } = useQuery(
    GetParticipantFieldsForImportQuery,
    {
      variables: {
        project_id: project.id,
        event_id: form.values.event.id,
      },
      skip: !form.values.event.id,
    },
  );

  const event = data?.project.event;
  const fields = event?.participant_fields || [];

  const addParticipantFieldImportSchema = useMemo(() => yup.object({
    event: yup.object({
      id: yup.string().required(),
    }),
    ...importParticipantFieldEntries ? {
      participant_field: yup.object({
        id: yup.string().required(),
      }),
    } : {},
    data: yup.mixed().required(),
  }), [importParticipantFieldEntries]);

  const { errors, valid } = useValidation({
    schema: addParticipantFieldImportSchema,
    values: form.values,
    externalErrors: getServerErrors(error),
  });

  const selectedField = fields.filter((field) => field.id === form.values.participant_field?.id)[0];

  return (
    <UI.Form onSubmit={() => addImport()}>
      <UI.FormGrid>
        <UI.Div>
          <UI.H4>
            {t('add_import')}
          </UI.H4>
          <UI.InputDescription sc={{ mb: 1 }}>
            {t('add_import_description')}
          </UI.InputDescription>
        </UI.Div>

        <UI.ServerErrors error={error} />

        <UI.HR />

        <UI.InputGroup>
          <UI.InputLabel htmlFor="ImportTypeParticipants">
            {t('import_type')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          <UI.Radio
            checked={importParticipants}
            onChange={() => form.set('type', 'participants')}
            id="ImportTypeParticipants"
          >
            {t('participants')}
          </UI.Radio>
          <UI.Radio
            checked={importParticipantFieldEntries}
            onChange={() => form.set('type', 'participant_field_entries')}
          >
            {t('participant_field_entries')}
          </UI.Radio>
        </UI.InputGroup>

        {form.values.type && (
          <UI.FadeIn>
            <UI.FormGrid>
              <UI.InputGroup>
                <UI.InputLabel htmlFor="Event">
                  {t('event')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <EventPicker
                  eventId={form.values.event.id}
                  onChange={(event) => form.set((values) => ({
                    ...values,
                    event: { id: event.id },
                    participant_field: importParticipantFieldEntries ? { id: null } : null,
                  }))}
                  buttonId="Event"
                  nullable={false}
                />
              </UI.InputGroup>

              {importParticipants && (
                <ParticipantFieldImportExample />
              )}

              {importParticipantFieldEntries && (
                <>
                  <UI.InputGroup sc={{ valid: !errors.participant_field, touched: form.touched.participant_field }}>
                    <UI.InputLabel htmlFor="ParticipantField">
                      {t('field')}
                      {' '}
                      <UI.RequiredMark />
                    </UI.InputLabel>
                    <UI.Select
                      {...form.select('participant_field.id', fields.map((field) => field.id))}
                      id="ParticipantField"
                      disabled={fields.length === 0}
                    >
                      {!form.values.participant_field?.id && <option value=""> </option>}
                      {fields.map((field, index) => (
                        <option {...form.option(index)} key={index}>{field.title}</option>
                      ))}
                    </UI.Select>
                    <UI.ErrorMessages
                      attribute={t('field')}
                      errors={errors.participant_field}
                    />
                  </UI.InputGroup>

                  {!selectedField && (
                    <UI.Info>
                      {t('select_a_field_to_see_import_example')}
                      <br />
                      <UI.A
                        href="https://atleta.cc/article/import-custom-fields"
                        target="_blank"
                        rel="noopener noreferer"
                      >
                        {t('how_to_import_participant_field_entries')}
                        {' '}
                        <UI.Icon>
                          <ExternalLink />
                        </UI.Icon>
                      </UI.A>
                    </UI.Info>
                  )}

                  {selectedField && (
                    <ParticipantFieldImportExample field={selectedField} event={event} />
                  )}
                </>
              )}

              <UI.InputGroup sc={{ valid: !errors.data, touched: form.touched.data }}>
                <UI.InputLabel htmlFor="Data">
                  {t('data')}
                  {' '}
                  <UI.RequiredMark />
                </UI.InputLabel>
                <UI.InputDescription>
                  {t('participant_field_import_file_description')}
                </UI.InputDescription>
                <UI.Input {...form.file('data')} id="Data" />
              </UI.InputGroup>
            </UI.FormGrid>
          </UI.FadeIn>
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('upload')}
          loading={loading}
          valid={valid}
          onClose={onCancel}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

interface UseFinalizeParticipantFieldImportModalProps {
  eventId?: string;
  onSuccess?: (participantFieldImport: ParticipantFieldImport) => void;
}

export const useAddParticipantFieldImportModal = ({
  eventId, onSuccess,
}: UseFinalizeParticipantFieldImportModalProps) => {
  const [openModal, closeModal] = useModal(() => {
    const handleSuccess = (participantFieldImport: ParticipantFieldImport) => {
      closeModal();
      onSuccess?.(participantFieldImport);
    };

    return (
      <UI.Modal maxWidth={600} close={closeModal}>
        <AddParticipantFieldImportForm
          eventId={eventId}
          onSuccess={handleSuccess}
          onCancel={closeModal}
        />
      </UI.Modal>
    );
  }, [eventId]);

  return [openModal, closeModal];
};

export default AddParticipantFieldImportForm;
